/**
 * useNotificationSocket
 *
 * Manages a Socket.IO connection to /ws/notifications for real-time
 * notification push from the API server.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';

export interface NotificationEvent {
  type: string;
  conversationId?: string;
  clinicId?: string;
  notificationId?: string;
  timestamp?: string;
}

interface UseNotificationSocketOptions {
  enabled?: boolean;
}

interface UseNotificationSocketReturn {
  isConnected: boolean;
  onNotification: (callback: (event: NotificationEvent) => void) => () => void;
}

export function useNotificationSocket(
  options: UseNotificationSocketOptions = {},
): UseNotificationSocketReturn {
  const { enabled = true } = options;
  const socketRef = useRef<Socket | null>(null);
  const callbacksRef = useRef<Set<(event: NotificationEvent) => void>>(new Set());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    // Derive WebSocket URL from API base URL (strip /api/ suffix)
    const apiBase =
      import.meta.env.VITE_API_BASE_URL?.replace(/\/api\/?$/, '') ||
      'http://localhost:3001';

    const socket = io(apiBase, {
      path: '/ws/notifications',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.on('connect_error', () => {
      setIsConnected(false);
    });

    socket.on('notification:new', (data: NotificationEvent) => {
      for (const cb of callbacksRef.current) {
        try {
          cb(data);
        } catch {
          // Ignore callback errors
        }
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [enabled]);

  const onNotification = useCallback(
    (callback: (event: NotificationEvent) => void) => {
      callbacksRef.current.add(callback);
      return () => {
        callbacksRef.current.delete(callback);
      };
    },
    [],
  );

  return { isConnected, onNotification };
}
